import type { z } from 'zod'

import { clearStoredSession, getStoredSession } from '@/auth/session'

import { ApiError } from './errors'
import { handleMockRequest } from './mock/server'

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'

export interface ApiRequest {
  method: HttpMethod
  /** Percorso relativo alla base, es. "/collections/c1/items". */
  path: string
  query?: Record<string, QueryValue | undefined>
  body?: unknown
}

/**
 * Un valore in query string. Le liste servono ai filtri multi-valore e si
 * serializzano separate da virgola — il server le rilegge così. Restano
 * leggibili a occhio nudo, cosa che conta: esiste un dev server costruito
 * apposta per provare le chiamate a mano.
 */
export type QueryValue = string | number | boolean | readonly string[]

const API_MODE = (import.meta.env.VITE_API_MODE as string | undefined) ?? 'mock'
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api'

function buildPath({ path, query }: ApiRequest): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','))
      continue
    }
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

async function sendOverHttp(request: ApiRequest): Promise<unknown> {
  const session = getStoredSession()
  const headers: Record<string, string> = {}
  if (request.body) headers['Content-Type'] = 'application/json'
  if (session) headers.Authorization = `Bearer ${session.token}`

  let response: Response
  try {
    // Niente `credentials: 'include'`: l'autenticazione è a Bearer token,
    // non a cookie — e con CORS aperto a `*` i browser rifiutano comunque le
    // richieste con credenziali per via delle specifiche.
    response = await fetch(`${API_BASE_URL}${buildPath(request)}`, {
      method: request.method,
      headers,
      body: request.body ? JSON.stringify(request.body) : undefined,
    })
  } catch {
    throw new ApiError('network', 'Il server non ha risposto.', 0)
  }

  const payload: unknown = response.status === 204 ? null : await response.json().catch(() => null)

  if (!response.ok) {
    if (response.status === 401) clearStoredSession()

    const problem = (payload ?? {}) as { code?: string; message?: string }
    throw new ApiError(
      (problem.code as ApiError['code']) ?? 'unknown',
      problem.message ?? 'Richiesta non riuscita.',
      response.status,
    )
  }

  return payload
}

/**
 * Unico punto d'ingresso verso i dati.
 *
 * Gli endpoint non sanno se stanno parlando con l'adapter in memoria o con un
 * backend vero: cambia una variabile d'ambiente, non una riga di codice
 * applicativo. La risposta viene sempre validata contro lo schema.
 */
export async function request<T>(schema: z.ZodType<T>, apiRequest: ApiRequest): Promise<T> {
  const payload =
    API_MODE === 'mock' ? await handleMockRequest(apiRequest) : await sendOverHttp(apiRequest)

  const parsed = schema.safeParse(payload)
  if (!parsed.success) {
    throw new ApiError(
      'validation',
      `Risposta inattesa da ${apiRequest.method} ${apiRequest.path}.`,
      500,
      { issues: parsed.error.issues },
    )
  }

  return parsed.data
}
